import type { WrittenPage } from '../sections'
import { STACK_GROUPS_EN } from '../stack'

/**
 * Enterprise AI Solutions — Halil's page 4. Forward Deployed Engineers, Agent Hub
 * and Agent Core, one flowing page.
 *
 * One structural edit: no separate "How we work" section, for the same reason as
 * the Transformation Management page — it is the third telling of the same idea.
 * The Agentic Platform line is folded into the closing instead.
 */
export const enterpriseAiSolutions: WrittenPage = {
  navLabel: 'Enterprise AI Solutions',
  title: 'Enterprise AI Solutions: Agent Hub, Agent Core',
  description:
    'Agentic AI for enterprises on SAP, Oracle, IFS, Workday and Salesforce. Agent Core built once, Agent Hub built per use case, engineers who ship.',
  sections: [
    {
      kind: 'hero',
      headline:'Your applications hold the process. Your agents need the authority.',
      sub: 'Deploy agentic AI at scale, on enterprise-grade infrastructure, with the delegation boundary decided by the business rather than by a vendor.',
      photo: {
        src: '/photo/overhead-table-charts.jpg',
        alt: 'Forward deployed engineers and business colleagues around one table seen from above, working through architecture diagrams on laptops and paper.',
      },
      ctas: [{ label: 'Book a Call', href: '#contact' }],
    },
    {
      kind: 'stack',
      groups: STACK_GROUPS_EN,
      check:
        'THREE MARKS STILL MISSING: IFS, DeepSeek and Alibaba. Kagan sourced the ' +
        'other twelve on 2026-08-25 and they render as logos now; these three ' +
        'render as plain-text chips, which reads fine among the others but is a ' +
        'gap rather than a choice. Drop ifs.webp, deepseek.webp and alibaba.webp ' +
        'into apps/website/logo-src/ and run `pnpm --filter @digitopia/website ' +
        'images` — nothing in the content or the component changes. Pull each ' +
        'from that vendor\'s own brand/press kit, never a recreation.\n\n' +
        'Two of the twelve are worth a second look before launch. SALESFORCE is ' +
        'the square cloud icon, which at this row\'s 20px height reads as a small ' +
        'blue blob beside seven wordmarks — their horizontal lockup would sit ' +
        'better. AWS is the official "Powered by AWS" badge, so the "powered by" ' +
        'line above the wordmark is sub-pixel here; that is the file AWS requires ' +
        'for this use, so the question is whether to give it more height rather ' +
        'than whether to swap it.\n\n' +
        'And a factual one, separate from the logos: confirm "integrated" / ' +
        '"connected" / "supported" describe today\'s Agent Core rather than the ' +
        'roadmap. The Enterprise AI Solutions page already lists SAP, Oracle, IFS, ' +
        'Workday and Salesforce in its own description, so those five are covered — ' +
        'the rest of this row is a new claim.'
    },
    {
      kind: 'prose',
      heading: 'Why agentic, and why now.',
      paragraphs: [
        'A chatbot answers. An agent acts. The moment software can act inside your core systems, the hard question stops being technical and becomes a governance question. What is it allowed to do. Who owns the outcome. When does a human decide. And how do you prove any of it afterwards.',
        'Plenty of enterprises now have a shelf of promising demos and very little in production. The gap is rarely model quality. It is the absence of a governed place to put an agent to work.',
      ],
      pullquote: 'Pilots are easy, because nothing is at stake. Scale is hard, because everything is.',
    },
    {
      kind: 'features',
      heading: 'Enterprise AI Solutions Architecture',
      columns: 2,
      textSize: 'lg',
      items: [
        {
          title: 'Agent Core',
          note: 'Built once, shared.',
          body: 'The enterprise-grade agentic infrastructure. It is what the system is able to do. Harness and agent runtime: the agentic loop, context assembly, sandboxing, durability, orchestration. Knowledge plane: data and lineage, semantics and ontology, agent memory. Integration and action gateway: registry, tool broker, MCP, A2A, APIs, RPA. Inference and model routing: model portfolio, gateway, caching, batching, fallback.',
        },
        {
          title: 'Agent Hub',
          note: 'Built per use case.',
          body: 'The agent design and run environment. It is what the business has authorized the system to do. Policy and the delegation boundary: what stays deterministic code, what is delegated to model judgment. Agents: roles, skills, tool grants, sub-agent topology, handoffs. Human authority and interaction: delegated decision rights, approval gates, escalation paths, audit trail.',
        },
      ],
      footnote:
        'We build your Agent Core once. Your Agent Hub is where the business decides what to trust it with.',
      check:
        'Agent Core, Agent Hub and Agentic Platform are used as product names throughout ' +
        'this page and in the meta title. Confirm these are the final external names, and ' +
        'whether any should be trademarked before launch.',
    },
    {
      kind: 'features',
      heading: 'Enterprise AI Foundation',
      columns: 4,
      items: [
        { title: 'Assurance and GRC', body: 'Controls that a risk committee will accept, evidenced rather than asserted.' },
        { title: 'Identity and authorization', body: 'Agents get identities and scoped permissions, like any other actor in your estate.' },
        { title: 'Evaluation and observability', body: 'You can see what the agent did, why, and whether it is getting better or worse.' },
        { title: 'FinOps and unit economics', body: 'Cost per outcome, not cost per token. The number a CFO can hold you to.' },
      ],
    },
    {
      kind: 'list',
      heading: 'Forward Deployed Engineers',
      intro:
        'FDEs sit with your business people and your engineers, inside your processes and your codebase. They ship Agent Core into your estate and Agent Hub into your workflows, alongside AI Engineers tuning the models, context and evaluations behind them.',
      variant: 'bullet',
      items: [
        'Define the outcome as a measurable unit of value, not a use case title',
        'Stand up enough Agent Core to carry the first live workflow',
        'Put one agent into production behind a human approval gate',
        'Build the evaluation harness before widening the boundary',
        'Scale to the next use cases on infrastructure that already exists',
      ],
      cta: { label: 'Book a Call', href: '#contact' },
    },
    {
      kind: 'features',
      heading: 'Everything connects through one governed surface.',
      columns: 3,
      items: [
        { title: 'Core systems', body: 'SAP, Oracle, IFS, Workday, Salesforce, Databricks, Snowflake.' },
        { title: 'Ecosystem', body: 'Customers, suppliers, logistics partners, bank integrations, regulator integrations, third parties.' },
        { title: 'Operations', body: 'Plants, warehouses, stores and branches, logistics, and the processes that run across them.' },
      ],
      footnote:
        'The integration and action gateway is the governed tool surface and the real security perimeter. It is the reason you can extend agents into your ERP without extending your risk.',
    },
    /*
      No closing dark CTA band here, asked for 2026-08-30: "form olan herhangi
      bir sayfada formun hemen üstünde genelde bu siyah kutu oluyor, bunlar
      olmasın". The closing form already sits at the foot of this page.
    */
  ],
}
